import { createContext, useContext } from 'react';

var FieldType;
(function (FieldType) {
    FieldType["Number"] = "Number";
    FieldType["String"] = "String";
    FieldType["Structure"] = "Structure";
    FieldType["ID"] = "ID";
    FieldType["Computed"] = "Computed";
})(FieldType || (FieldType = {}));

/* eslint-disable @typescript-eslint/no-use-before-define */
var ExpressionType;
(function (ExpressionType) {
    ExpressionType["Identifier"] = "Identifier";
    ExpressionType["BinaryExpression"] = "BinaryExpression";
    ExpressionType["CallExpression"] = "CallExpression";
    ExpressionType["Literal"] = "Literal";
    ExpressionType["LogicalExpression"] = "LogicalExpression";
    ExpressionType["UnaryExpression"] = "UnaryExpression";
})(ExpressionType || (ExpressionType = {}));

var DesirabilityFunctionType;
(function (DesirabilityFunctionType) {
    DesirabilityFunctionType["linear"] = "linear";
    DesirabilityFunctionType["unitStep"] = "unitStep";
    DesirabilityFunctionType["rectangular"] = "rectangular";
    DesirabilityFunctionType["triangular"] = "triangular";
    DesirabilityFunctionType["logarithmic"] = "logarithmic";
    DesirabilityFunctionType["sigmoid"] = "sigmoid";
    DesirabilityFunctionType["custom"] = "custom";
    DesirabilityFunctionType["discrete"] = "discrete";
})(DesirabilityFunctionType || (DesirabilityFunctionType = {}));

const EMPTY_DECISION_TREE = {
    name: '',
    data: {
        layout: 'Auto',
        nodes: [],
        arrows: [],
    },
};

var ReasonType;
(function (ReasonType) {
    ReasonType["DoNotCalculate"] = "DoNotCalculate";
    ReasonType["FilteredOff"] = "FilteredOff";
    ReasonType["ReplacedWithX"] = "ReplacedWithX";
    ReasonType["ReplacedWithY"] = "ReplacedWithY";
    ReasonType["ReplacedWithColumn"] = "ReplacedWithColumn";
})(ReasonType || (ReasonType = {}));

var ResponseErrors;
(function (ResponseErrors) {
    ResponseErrors["NOT_FOUND"] = "NOT_FOUND";
    ResponseErrors["UNKNOWN"] = "UNKNOWN";
})(ResponseErrors || (ResponseErrors = {}));

const MoosaDataContext = createContext(null);
function useMoosaDataContext() {
    const context = useContext(MoosaDataContext);
    if (!context) {
        throw Error('IMoosaDataService is not provided in MoosaDataContext');
    }
    return context;
}

var SpecialColumn;
(function (SpecialColumn) {
    SpecialColumn["RowNumber"] = "RowNumber";
    SpecialColumn["Score"] = "Score";
    SpecialColumn["Radar"] = "Radar";
    SpecialColumn["NotUsed"] = "NotUsed";
    SpecialColumn["Missing"] = "Missing";
    SpecialColumn["Comparison"] = "Comparison";
})(SpecialColumn || (SpecialColumn = {}));
function isScoringType(type, isDiscreteColumn) {
    return (type === FieldType.Number ||
        (type === FieldType.String && isDiscreteColumn) ||
        type === FieldType.Computed);
}
function isScoringColumn(col) {
    return isScoringType(col === null || col === void 0 ? void 0 : col.type, col === null || col === void 0 ? void 0 : col.isDiscreteColumn);
}
function isNumericColumn(col) {
    return (col === null || col === void 0 ? void 0 : col.type) === FieldType.Number || (col === null || col === void 0 ? void 0 : col.type) === FieldType.Computed;
}

var EMoosaSarConfigImageSize;
(function (EMoosaSarConfigImageSize) {
    EMoosaSarConfigImageSize["Small"] = "small";
    EMoosaSarConfigImageSize["Medium"] = "medium";
    EMoosaSarConfigImageSize["Large"] = "large";
    EMoosaSarConfigImageSize["ExtraLarge"] = "extraLarge";
})(EMoosaSarConfigImageSize || (EMoosaSarConfigImageSize = {}));

var JobStatus;
(function (JobStatus) {
    JobStatus["NotStarted"] = "";
    JobStatus["Started"] = "Started";
    JobStatus["Finished"] = "Finished";
    JobStatus["Failed"] = "Failed";
    JobStatus["Deleted"] = "Deleted";
})(JobStatus || (JobStatus = {}));

var SarMatrixCellView;
(function (SarMatrixCellView) {
    SarMatrixCellView["Circles"] = "Circles";
    SarMatrixCellView["PieChart"] = "PieChart";
    SarMatrixCellView["FactorsAbsolute"] = "FactorsAbsolute";
})(SarMatrixCellView || (SarMatrixCellView = {}));

var MMPView;
(function (MMPView) {
    MMPView["Table"] = "Table";
    MMPView["RadiantChart"] = "Radiant Chart";
    MMPView["MultipleRadiantChart"] = "Multiple Radiant Chart";
})(MMPView || (MMPView = {}));

/**
 *
 * @returns
 *  value in [0, 1],
 *  or null - not calculated due to missing values,
 *  or undefined - yet not calculated
 */
function getSubstanceScoreValue(scores, rowId) {
    var _a;
    return (_a = scores[rowId]) === null || _a === void 0 ? void 0 : _a.value;
}
/**
 * Factor = weight * desirability_function(property_value)
 * columnIndex -  the index of the column in the dataset columns array
 */
function getScoringFactorByColumnId(scores, columnIndexes, rowId, colId) {
    var _a;
    const columnIndex = columnIndexes[colId];
    return (_a = scores[rowId]) === null || _a === void 0 ? void 0 : _a.factors[columnIndex];
}
/**
 * Raw Factor = desirability_function(property_value)
 * columnIndex -  the index of the column in the dataset columns array
 */
function getRawScoringFactorByColumnId(scores, columnIndexes, rowId, colId) {
    var _a;
    const columnIndex = columnIndexes[colId];
    return (_a = scores[rowId]) === null || _a === void 0 ? void 0 : _a.rawValues[columnIndex];
}
/**
 * Convert column indexes in scoring factors to corresponding columnIds
 */
function getRawScoringFactorsWithColumnIds(columnIds, score) {
    var _a;
    return Object.keys((_a = score === null || score === void 0 ? void 0 : score.rawValues) !== null && _a !== void 0 ? _a : {}).map((columnIndex) => [
        columnIds[+columnIndex],
        score === null || score === void 0 ? void 0 : score.rawValues[+columnIndex],
    ]);
}

var SortFunctionTableType;
(function (SortFunctionTableType) {
    SortFunctionTableType["Ascending"] = "Ascending";
    SortFunctionTableType["Descending"] = "Descending";
    SortFunctionTableType["None"] = "None";
})(SortFunctionTableType || (SortFunctionTableType = {}));

function getCellValue(row, colId) {
    return row ? row[colId] : undefined;
}
/**
 * Note: the dataset may contain several or none structure columns, the function returns the first one
 */
const getStructureColumnId = (columnsInfo) => {
    var _a;
    return (_a = columnsInfo.find((col) => col.type === FieldType.Structure)) === null || _a === void 0 ? void 0 : _a.name;
};
/**
 * Note: the dataset may contain several or none ID columns, the function returns the first one
 */
const getIdentifierColumnId = (columnsInfo) => {
    var _a;
    return (_a = columnsInfo.find((col) => col.type === FieldType.ID)) === null || _a === void 0 ? void 0 : _a.name;
};

var CellBackgroundMode;
(function (CellBackgroundMode) {
    CellBackgroundMode["None"] = "None";
    CellBackgroundMode["Histogram"] = "Histogram";
    CellBackgroundMode["Heatmap"] = "Heatmap";
})(CellBackgroundMode || (CellBackgroundMode = {}));
var RowSizes;
(function (RowSizes) {
    RowSizes["SMALL"] = "small";
    RowSizes["MEDIUM"] = "medium";
    RowSizes["BIG"] = "big";
})(RowSizes || (RowSizes = {}));

var StructureType;
(function (StructureType) {
    StructureType[StructureType["SMILES"] = 0] = "SMILES";
    StructureType[StructureType["MOLFILE"] = 1] = "MOLFILE";
})(StructureType || (StructureType = {}));

var DTNodeType;
(function (DTNodeType) {
    DTNodeType["Property"] = "Property";
    DTNodeType["And"] = "And";
    DTNodeType["Or"] = "Or";
    DTNodeType["ALN"] = "AtLeastN";
    DTNodeType["AMX"] = "AtMostX";
    DTNodeType["Chart"] = "Chart";
    DTNodeType["Group"] = "Group";
})(DTNodeType || (DTNodeType = {}));
var DTOperation;
(function (DTOperation) {
    DTOperation["Greater"] = ">";
    DTOperation["Less"] = "<";
    DTOperation["GreaterEqual"] = "\u2265";
    DTOperation["LessEqual"] = "\u2264";
})(DTOperation || (DTOperation = {}));
var ConditionType;
(function (ConditionType) {
    ConditionType["Simple"] = "simple";
    ConditionType["Range"] = "range";
    ConditionType["Discrete"] = "discrete";
})(ConditionType || (ConditionType = {}));
var PortGroupingType;
(function (PortGroupingType) {
    PortGroupingType["Regular"] = "regular";
    PortGroupingType["TrueMissing"] = "trueMissing";
    PortGroupingType["FalseMissing"] = "falseMissing";
    PortGroupingType["HideMissing"] = "hideMissing";
})(PortGroupingType || (PortGroupingType = {}));
function isPropertyNode(node) {
    return node.type === DTNodeType.Property;
}
function isOrNode(node) {
    return node.type === DTNodeType.Or;
}
function isAndNode(node) {
    return node.type === DTNodeType.And;
}
function isAtLeastNNode(node) {
    return node.type === DTNodeType.ALN;
}
function isAtMostXNode(node) {
    return node.type === DTNodeType.AMX;
}
function isChartNode(node) {
    return node.type === DTNodeType.Chart;
}
function isGroupNode(node) {
    return node.type === DTNodeType.Group;
}

function isPropertyNodeGroups(node) {
    return node.type === DTNodeType.Property;
}
function isOrNodeGroups(node) {
    return node.type === DTNodeType.Or;
}
function isAndNodeGroups(node) {
    return node.type === DTNodeType.And;
}

function isPropertyNodeResult(node) {
    return node.type === DTNodeType.Property;
}
function isOrNodeResult(node) {
    return node.type === DTNodeType.Or;
}
function isAndNodeResult(node) {
    return node.type === DTNodeType.And;
}
function isAtLeastNNodeResult(node) {
    return node.type === DTNodeType.ALN;
}
function isAtMostXNodeResult(node) {
    return node.type === DTNodeType.AMX;
}
function isChartNodeResult(node) {
    return node.type === DTNodeType.Chart;
}

const RADAR_COLUMN_ID = 'radar';
const SCORE_COLUMN_ID = 'score';
const MISSING_VALUES_COLUMN_ID = 'missingColumns';
const COMPARISON_SCORE_COLUMN_ID = 'comparisonScore';
const CID = 'CID';
const ROW_NUMBER_COLUMN_ID = '__key';
const NEW_CALC_COLUMN_ID = 'new_calc_key';
const PAGE_SIZE = 100;
const MAX_DATASET = 30000;
const DEFAULT_COLUMN_NAMES = {
    [SCORE_COLUMN_ID]: 'Scoring Profile',
    [COMPARISON_SCORE_COLUMN_ID]: 'Comparison scoring',
    [MISSING_VALUES_COLUMN_ID]: 'Missing values',
    [RADAR_COLUMN_ID]: 'Radar',
    [CID]: 'CID',
};

export { CID, COMPARISON_SCORE_COLUMN_ID, CellBackgroundMode, ConditionType, DEFAULT_COLUMN_NAMES, DTNodeType, DTOperation, DesirabilityFunctionType, EMPTY_DECISION_TREE, EMoosaSarConfigImageSize, ExpressionType, FieldType, JobStatus, MAX_DATASET, MISSING_VALUES_COLUMN_ID, MMPView, MoosaDataContext, NEW_CALC_COLUMN_ID, PAGE_SIZE, PortGroupingType, RADAR_COLUMN_ID, ROW_NUMBER_COLUMN_ID, ReasonType, ResponseErrors, RowSizes, SCORE_COLUMN_ID, SarMatrixCellView, SortFunctionTableType, SpecialColumn, StructureType, getCellValue, getIdentifierColumnId, getRawScoringFactorByColumnId, getRawScoringFactorsWithColumnIds, getScoringFactorByColumnId, getStructureColumnId, getSubstanceScoreValue, isAndNode, isAndNodeGroups, isAndNodeResult, isAtLeastNNode, isAtLeastNNodeResult, isAtMostXNode, isAtMostXNodeResult, isChartNode, isChartNodeResult, isGroupNode, isNumericColumn, isOrNode, isOrNodeGroups, isOrNodeResult, isPropertyNode, isPropertyNodeGroups, isPropertyNodeResult, isScoringColumn, isScoringType, useMoosaDataContext };
//# sourceMappingURL=index.js.map
